import React, { useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSavedTemplates } from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { useDeleteMigrationTemplate } from "../../hooks";

const SavedTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  // Fetch real data from API
  const { data, error, refetch } = useSavedTemplates(
    currentPage,
    PAGE_SIZE,
    currentSearch
  );
  const { mutate: deleteTemplate, isPending: isDeleting } =
    useDeleteMigrationTemplate(currentPage, PAGE_SIZE, currentSearch);

  // Column definitions for the table
  const columns = [
    {
      key: "templateNo",
      header: "Template ID",
      type: "custom" as const,
    },
    {
      key: "name",
      header: "Template Name",
      type: "custom" as const,
    },
    {
      key: "dataTypeDisplay",
      header: "Data Type",
      type: "badge" as const,
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      type: "custom" as const,
    },
    {
      key: "createdDate",
      header: "Created On",
      type: "custom" as const,
    },
    {
      key: "usageCount",
      header: "Usage",
      type: "custom" as const,
    },
  ];

  const actions = [
    {
      label: "View Details",
      icon: "Eye" as const,
      onClick: (item: any) => {
        navigate(
          `/onboarding/data-migration/saved-templates/details/${item.id}`
        );
      },
    },
    {
      label: "Use Template",
      icon: "FileUp" as const,
      onClick: (row: any) => {
        // Navigate to data upload with template pre-loaded
        navigate("/onboarding/data-migration/file-upload", {
          state: {
            templateId: row.id,
            templateName: row.templateName,
            dataType: row.dataType,
            operation_type: 2, // two is for new upload
            categoryKey: row.dataType,
            uploadMethod: "template",
          },
        });
      },
    },
    {
      label: "Delete",
      icon: "Trash" as const,
      onClick: async (row: any) => {
        deleteTemplate(row.id);
      },
    },
  ];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    }),
    [currentPage, totalPages, totalItems, startItem, endItem]
  );

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="space-y-2">
        <PageHeader
          title="Saved Templates"
          subtitle="Saved templates allow you to reuse successful field mappings for future imports."
        />
      </div>

      {/* Table */}
      <GenericTable
        data={data?.result || []}
        actions={actions}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search templates..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No saved templates found. Save a template from the mapping screen to see it here."
      />
    </div>
  );
};

export default SavedTemplates;
